<template>
  <div class="cs4-wrap">
    <div class="cs4-form-card">
      <div class="cs4-page-header">
        <div>
          <div class="cs4-page-subtitle">
            버튼 이미지와 문구를 설정하세요. 버튼은 최대 4개까지 설정 가능합니다.
          </div>
        </div>
      </div>

      <div class="cs4-editor-wrap">
        <div class="cs4-editor-scroll">
          <div
            v-for="(buttonItem, index) in localButtons"
            :key="buttonItem.slotNo"
            class="cs4-block"
          >
            <div class="cs4-block-title-row">
              <div class="cs4-block-badge">
                {{ index + 1 }} 번
              </div>

              <button
                v-if="localButtons.length > 1"
                class="cs4-delete-chip"
                type="button"
                @click="removeButton(index)"
              >
                <span class="cs4-delete-x">×</span>
                <span>삭제</span>
              </button>
            </div>

            <div class="cs4-card">
              <div
                class="cs4-top-text"
                :style="{
                  backgroundColor: buttonItem.bgColor,
                  color: buttonItem.textColor
                }"
              >
                <input
                  v-model="buttonItem.topText"
                  class="cs4-text-input"
                  :style="{ color: buttonItem.textColor }"
                  type="text"
                  placeholder="상단 문구 입력"
                />
              </div>

              <div class="cs4-image-area">
                <input
                  :ref="setFileInputRef(index)"
                  class="cs4-hidden-file"
                  type="file"
                  accept="image/*"
                  @change="onImageChange($event, index)"
                />

                <template v-if="buttonItem.image">
                  <div class="cs4-preview-wrap">
                    <img
                      :src="buttonItem.image.previewUrl"
                      class="cs4-preview-image"
                      alt="button preview"
                    />

                    <div class="cs4-image-action-row">
                      <button class="cs4-mini-btn" type="button" @click="openFileDialog(index)">
                        사진 변경
                      </button>

                      <button class="cs4-mini-btn cs4-danger" type="button" @click="removeImage(index)">
                        삭제
                      </button>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <button class="cs4-create-btn" type="button" @click="openFileDialog(index)">
                    <span class="cs4-plus-circle">+</span>
                    <span class="cs4-create-label">이미지 추가</span>
                  </button>
                </template>
              </div>

              <div
                class="cs4-bottom-text"
                :style="{
                  backgroundColor: buttonItem.bgColor,
                  color: buttonItem.textColor
                }"
              >
                <input
                  v-model="buttonItem.bottomText"
                  class="cs4-text-input"
                  :style="{ color: buttonItem.textColor }"
                  type="text"
                  placeholder="하단 문구 입력"
                />
              </div>

              <div class="cs4-style-panel ppt-style-panel">
                <div class="cs4-color-section">
                  <div class="cs4-color-header">
                    <span>배경색</span>
                    <span class="cs4-current-color" :style="{ backgroundColor: buttonItem.bgColor }"></span>
                  </div>

                  <div class="cs4-color-palette">
                    <button
                      v-for="color in themeColors"
                      :key="`bg_${buttonItem.slotNo}_${color}`"
                      type="button"
                      class="cs4-color-chip"
                      :class="{ active: normalizeColor(buttonItem.bgColor) === normalizeColor(color) }"
                      :style="{ backgroundColor: color }"
                      :title="color"
                      @click="setButtonColor(index, 'bgColor', color)"
                    ></button>
                  </div>

                  <ColorPicker
                    v-model:pureColor="buttonItem.bgColor"
                    format="hex"
                  />
                </div>

                <div class="cs4-color-section">
                  <div class="cs4-color-header">
                    <span>글자색</span>
                    <span class="cs4-current-color" :style="{ backgroundColor: buttonItem.textColor }"></span>
                  </div>

                  <div class="cs4-color-palette">
                    <button
                      v-for="color in textColors"
                      :key="`text_${buttonItem.slotNo}_${color}`"
                      type="button"
                      class="cs4-color-chip"
                      :class="{ active: normalizeColor(buttonItem.textColor) === normalizeColor(color) }"
                      :style="{ backgroundColor: color }"
                      :title="color"
                      @click="setButtonColor(index, 'textColor', color)"
                    ></button>
                  </div>

                  <ColorPicker
                    v-model:pureColor="buttonItem.textColor"
                    format="hex"
                  />
                </div>
              </div>
            </div>
          </div>

          <div v-if="localButtons.length < 4" class="cs4-block cs4-add-block-wrap">
            <div class="cs4-block-title-row cs4-block-title-row-placeholder"></div>

            <button class="cs4-add-block" type="button" @click="addButton">
              <div class="cs4-add-card">
                <div class="cs4-add-plus">+</div>
                <div class="cs4-add-title">버튼 추가</div>
                <div class="cs4-add-desc">최대 4개까지 가능합니다.</div>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div class="cs4-bottom-actions">
        <button class="cs4-btn cs4-btn-secondary" type="button" @click="onBack">이전으로</button>
        <button class="cs4-btn cs4-btn-primary" type="button" @click="onNext" :disabled="loading">
          다음으로
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'
import { ColorPicker } from 'vue3-colorpicker'
import 'vue3-colorpicker/style.css'

export default {
  name: "CreateSiteStep4",

  components: {
    ColorPicker,
  },

  props: {
    mode: {
      type: String,
      default: "create",
    },
    formValue: {
      type: Object,
      default() {
        return {}
      },
    },
  },

  data() {
    return {
      fileInputRefs: {},
      localButtons: [],
      newImageSequence: 1,
      loading: false,

      themeColors: [
        '#000000', '#ffffff', '#ef4444', '#f97316', '#eab308',
        '#22c55e', '#3b82f6', '#2563eb', '#6366f1', '#8b5cf6',
        '#ec4899', '#64748b', '#9ca3af', '#f3f4f6'
      ],

      textColors: [
        '#000000', '#ffffff', '#ef4444', '#f97316', '#eab308',
        '#22c55e', '#3b82f6', '#2563eb', '#6366f1', '#8b5cf6',
        '#ec4899', '#64748b'
      ],
    }
  },

  async mounted() {
    await this.initializeButtons()
  },

  watch: {
    formValue: {
      deep: true,
      async handler() {
        await this.initializeButtons()
      },
    },
  },

  beforeUnmount() {
    this.revokeAllPreviewUrls()
  },

  methods: {
    normalizeColor(color) {
      return String(color || '').trim().toLowerCase()
    },

    setButtonColor(index, key, color) {
      const target = this.localButtons[index]
      if (!target) return

      target[key] = color
    },

    getDefaultColors() {
      const rootStyle = getComputedStyle(document.documentElement)

      return {
        bgColor: rootStyle.getPropertyValue("--color-primary-soft").trim() || "#f6d8d8",
        textColor: rootStyle.getPropertyValue("--color-primary-border").trim() || "#ef4444",
      }
    },

    createButtonItem(slotNo) {
      const colors = this.getDefaultColors()

      return {
        id: null,
        slotNo,
        buttonNo: slotNo,

        topText: "",
        bottomText: "",

        bgColor: colors.bgColor,
        textColor: colors.textColor,

        fileNo: null,
        useYn: true,

        image: null,
      }
    },

    createBlobPreviewUrl(file) {
      if (!file) return ""
      return URL.createObjectURL(file)
    },

    buildButtonItemFromDetail(item, index) {
      const firstImage = Array.isArray(item.buttonImageList) && item.buttonImageList.length
        ? item.buttonImageList[0]
        : null

      return {
        id: item.id ?? null,
        slotNo: index + 1,
        buttonNo: item.buttonNo ?? index + 1,

        topText: item.topText ?? "",
        bottomText: item.bottomText ?? "",

        bgColor: item.bgColor ?? item.topBgColor ?? item.bottomBgColor ?? this.getDefaultColors().bgColor,
        textColor: item.textColor ?? item.topTextColor ?? item.bottomTextColor ?? this.getDefaultColors().textColor,

        fileNo: item.fileNo ?? null,
        useYn: item.useYn ?? true,

        image: firstImage
          ? {
              tempId: `old_${item.buttonNo}_${firstImage.attachFileNo ?? 1}`,
              attachFileNo: firstImage.attachFileNo ?? null,
              file: null,
              previewUrl: firstImage.fileUrl ?? "",
              name: firstImage.originalFileName ?? firstImage.fileName ?? "",
              isNew: false,
            }
          : null,
      }
    },

    async initializeButtons() {
      const buttonsFromForm = Array.isArray(this.formValue?.buttons)
        ? this.formValue.buttons
        : []

      if (buttonsFromForm.length) {
        this.applyFormValue(this.formValue)
        return
      }

      if (this.mode === "edit" && this.formValue?.siteId) {
        await this.fetchStep4Detail()
        return
      }

      this.revokeAllPreviewUrls()
      this.localButtons = [this.createButtonItem(1)]
    },

    async fetchStep4Detail() {
      this.loading = true

      try {
        const res = await api.get("/app/site/step4/detail", {
          params: {
            siteId: this.formValue.siteId,
          },
        })

        const result = res.data?.result
        const buttonItems = Array.isArray(result?.buttonItems) ? result.buttonItems : []

        this.revokeAllPreviewUrls()

        if (!buttonItems.length) {
          this.localButtons = [this.createButtonItem(1)]
          return
        }

        this.localButtons = buttonItems.slice(0, 4).map((item, index) => {
          return this.buildButtonItemFromDetail(item, index)
        })
      } catch (e) {
        console.error("step4 detail load failed", e)
        this.localButtons = [this.createButtonItem(1)]
      } finally {
        this.loading = false
      }
    },

    applyFormValue(formValue) {
      const sourceButtons = Array.isArray(formValue?.buttons) ? formValue.buttons : []

      this.revokeAllPreviewUrls()

      if (!sourceButtons.length) {
        this.localButtons = [this.createButtonItem(1)]
        return
      }

      this.localButtons = sourceButtons.slice(0, 4).map((item, index) => {
        let image = null

        if (item.image) {
          if (item.image.isNew && item.image.file) {
            image = {
              ...item.image,
              previewUrl: this.createBlobPreviewUrl(item.image.file),
            }
          } else {
            image = {
              ...item.image,
            }
          }
        }

        return {
          ...this.createButtonItem(index + 1),
          ...item,
          slotNo: index + 1,
          buttonNo: item.buttonNo ?? item.slotNo ?? index + 1,
          image,
        }
      })
    },

    buildButtonsForEmit() {
      return this.localButtons.map((item, index) => {
        let image = null

        if (item.image) {
          if (item.image.isNew) {
            image = {
              tempId: item.image.tempId,
              attachFileNo: null,
              file: item.image.file || null,
              previewUrl: null,
              name: item.image.name || "",
              isNew: true,
            }
          } else {
            image = {
              ...item.image,
            }
          }
        }

        return {
          id: item.id ?? null,
          slotNo: index + 1,
          buttonNo: item.buttonNo ?? index + 1,
          topText: item.topText ?? "",
          bottomText: item.bottomText ?? "",
          bgColor: item.bgColor ?? this.getDefaultColors().bgColor,
          textColor: item.textColor ?? this.getDefaultColors().textColor,
          fileNo: item.fileNo ?? null,
          useYn: item.useYn ?? true,
          image,
        }
      })
    },

    renumberButtons() {
      this.localButtons = this.localButtons.map((item, index) => ({
        ...item,
        slotNo: index + 1,
      }))
    },

    addButton() {
      if (this.localButtons.length >= 4) return
      this.localButtons.push(this.createButtonItem(this.localButtons.length + 1))
    },

    removeButton(index) {
      const target = this.localButtons[index]

      if (target?.image?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(target.image.previewUrl)
      }

      this.localButtons.splice(index, 1)
      this.renumberButtons()
    },

    setFileInputRef(index) {
      return (el) => {
        if (el) this.fileInputRefs[index] = el
      }
    },

    openFileDialog(index) {
      const input = this.fileInputRefs[index]
      if (input) input.click()
    },

    onImageChange(event, index) {
      const file = event.target.files?.[0]
      if (!file) return
      if (!file.type.startsWith("image/")) return

      const target = this.localButtons[index]

      if (target.image?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(target.image.previewUrl)
      }

      target.image = {
        tempId: `new_${Date.now()}_${this.newImageSequence++}`,
        attachFileNo: null,
        file,
        previewUrl: this.createBlobPreviewUrl(file),
        name: file.name,
        isNew: true,
      }

      event.target.value = ""
    },

    removeImage(index) {
      const target = this.localButtons[index]
      if (!target.image) return

      if (target.image.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(target.image.previewUrl)
      }

      target.image = null
    },

    revokeAllPreviewUrls() {
      this.localButtons.forEach((buttonItem) => {
        if (buttonItem.image?.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(buttonItem.image.previewUrl)
        }
      })
    },

    buildPayload() {
      return {
        siteId: this.formValue.siteId,
        buttonItems: this.localButtons.map((btn) => ({
          id: btn.id ?? null,
          buttonNo: btn.slotNo,
          topText: btn.topText,
          bottomText: btn.bottomText,

          bgColor: btn.bgColor,
          textColor: btn.textColor,

          topBgColor: btn.bgColor,
          bottomBgColor: btn.bgColor,
          topTextColor: btn.textColor,
          bottomTextColor: btn.textColor,

          fileNo: btn.fileNo ?? null,
          useYn: true,

          remainButtonImages:
            btn.image && !btn.image.isNew && btn.image.attachFileNo
              ? [{
                  attachFileNo: btn.image.attachFileNo,
                  sortOrder: 1,
                }]
              : [],

          newButtonImages:
            btn.image && btn.image.isNew
              ? [{
                  tempId: btn.image.tempId,
                  sortOrder: 1,
                }]
              : [],
        })),
      }
    },

    onBack() {
      this.$emit("back", {
        buttons: this.buildButtonsForEmit()
      })
    },

    async onNext() {
      if (!this.formValue?.siteId) {
        alert("siteId가 없습니다.")
        return
      }

      try {
        this.loading = true

        const payload = this.buildPayload()
        const formData = new FormData()

        formData.append("paramsObj", JSON.stringify(payload))

        this.localButtons.forEach((btn) => {
          if (btn.image?.isNew && btn.image?.file) {
            formData.append("buttonImageFileList", btn.image.file)
            formData.append("buttonImageTempIds", btn.image.tempId)
          }
        })

        await api.post("/app/site/step4/save", formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        })

        await this.fetchStep4Detail()

        this.$emit("next", {
          buttons: this.buildButtonsForEmit()
        })
      } catch (e) {
        console.error(e)
        alert("버튼 저장 실패")
      } finally {
        this.loading = false
      }
    }
  },
}
</script>

<style scoped>
.cs4-wrap {
  background: #ffffff;
  padding: 10px 22px 24px;
  box-sizing: border-box;
  height: 100%;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

.cs4-form-card {
  margin-top: 12px;
  background: #ffffff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.cs4-page-header {
  align-items: center;
  margin-bottom: 16px;
  padding: 0 8px;
  flex-shrink: 0;
}

.cs4-page-subtitle {
  text-align: center;
  margin-top: 8px;
  font-size: 17px;
  color: #6b7280;
}

.cs4-top-action-bar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.cs4-editor-wrap {
  flex: 1;
  min-height: 0;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  background: #fff;
  overflow: auto;
}

.cs4-editor-scroll {
  min-width: max-content;
  min-height: 100%;
  padding: 24px;
  display: flex;
  gap: 52px;
  align-items: flex-start;
  flex-wrap: wrap;
  box-sizing: border-box;
}

.cs4-block {
  width: 300px;
  min-width: 300px;
}

.cs4-block-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 10px;
  min-height: 34px;
}

.cs4-block-title-row-placeholder {
  visibility: hidden;
}

.cs4-block-title {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
}

.cs4-block-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.2px;
}

.cs4-delete-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border: 1px solid #f3d1d1;
  border-radius: 999px;
  background: #fff7f7;
  color: #dc2626;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
}

.cs4-delete-chip:hover {
  background: #fee2e2;
  border-color: #efb4b4;
}

.cs4-delete-x {
  font-size: 16px;
  line-height: 1;
  font-weight: 800;
}

.cs4-card {
  width: 300px;
  border: 1px solid #9ea2a5;
  background: #fff;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
}

.cs4-top-text,
.cs4-bottom-text {
  min-height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.cs4-bottom-text {
  margin-top: auto;
}

.cs4-text-input {
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  text-align: center;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  font-family: inherit;
}

.cs4-text-input::placeholder {
  color: inherit;
  opacity: 0.7;
}

.cs4-style-panel {
  padding: 14px 12px;
  border-top: 1px solid #e5e7eb;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 14px;
  background: #fafafa;
  flex-shrink: 0;
}

.cs4-style-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.cs4-style-row label {
  font-size: 14px;
  font-weight: 700;
  color: #374151;
}

.cs4-color-input {
  width: 42px;
  height: 32px;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}

.cs4-image-area {
  flex: 1;
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  background: #fff;
}

.cs4-hidden-file {
  display: none;
}

.cs4-preview-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.cs4-preview-image {
  width: 100%;
  height: 320px;
  object-fit: contain;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
}

.cs4-image-action-row {
  display: flex;
  gap: 10px;
  width: 100%;
}

.cs4-mini-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 46px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  cursor: pointer;
  font-size: 17px;
  font-weight: 700;
  color: #374151;
  font-family: inherit;
}

.cs4-mini-btn:hover {
  background: #f3f4f6;
}

.cs4-danger {
  color: #dc2626;
  border-color: #fecaca;
  background: #fff5f5;
}

.cs4-danger:hover {
  background: #fee2e2;
}

.cs4-create-btn {
  width: 100%;
  height: 320px;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  cursor: pointer;
}

.cs4-plus-circle {
  width: 82px;
  height: 82px;
  border-radius: 999px;
  border: 2px dashed #ff7b7b;
  color: #ff7b7b;
  font-size: 56px;
  line-height: 76px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cs4-create-label {
  font-size: 22px;
  font-weight: 700;
  color: #9ca3af;
}

.cs4-bottom-actions {
  margin-top: 18px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.cs4-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  font-family: inherit;
}

.cs4-btn:hover {
  background: #f3f4f6;
}

.cs4-btn-primary {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: #ffffff;
}

.cs4-btn-primary:hover {
  background: var(--color-primary-border);
  border-color: var(--color-primary-border);
}

.cs4-btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cs4-btn-secondary {
  background: #ffffff;
  border-color: #d1d5db;
  color: #111827;
}

.cs4-add-block {
  width: 300px;
  min-width: 300px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
}

.cs4-add-card {
  width: 300px;
  min-height: 520px;
  border: 1px dashed #d1d5db;
  border-radius: 16px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  transition: all 0.18s ease;
}

.cs4-add-block:hover .cs4-add-card {
  background: #f5f7fb;
  border-color: var(--color-primary);
}

.cs4-add-plus {
  width: 72px;
  height: 72px;
  border-radius: 999px;
  border: 2px dashed var(--color-primary);
  color: var(--color-primary);
  font-size: 42px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cs4-add-title {
  font-size: 22px;
  font-weight: 700;
  color: #374151;
}

.cs4-add-desc {
  font-size: 14px;
  color: #9ca3af;
}

@media (max-width: 1200px) {
  .cs4-wrap {
    padding: 10px 16px 20px;
  }

  .cs4-editor-scroll {
    gap: 28px;
    padding: 18px;
  }
}

.ppt-style-panel {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  padding: 14px 12px;
}

.cs4-color-section {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  padding: 10px;
}

.cs4-color-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #374151;
}

.cs4-current-color {
  width: 26px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #cbd5e1;
}

.cs4-color-palette {
  display: grid;
  grid-template-columns: repeat(7, 24px);
  gap: 7px;
  margin-bottom: 10px;
}

.cs4-color-chip {
  width: 24px;
  height: 24px;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
  cursor: pointer;
  padding: 0;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.cs4-color-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.16);
}

.cs4-color-chip.active {
  outline: 3px solid var(--color-primary);
  outline-offset: 2px;
}

.cs4-custom-color-btn {
  width: 100%;
  height: 32px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #f8fafc;
  color: #374151;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.cs4-custom-color-btn:hover {
  background: #f3f4f6;
  border-color: var(--color-primary);
}

.cs4-hidden-color-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
</style>